import React, { useState, useRef, useEffect, cloneElement } from 'react';
import ReactDOM from 'react-dom';

export const DropdownButton = ({
  label,
  children,
  disabled = false,
  className = '',
  buttonClassName = '',
  menuClassName = '',
  align = 'right', // 'left' or 'right'
  usePortal = false, // Render menu in portal to avoid overflow issues
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef();
  const buttonRef = useRef();
  const menuRef = useRef();
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [openDirection, setOpenDirection] = useState('down'); // 'down' or 'up'

  useEffect(() => {
    function handleClickOutside(event) {
      // Check if click is inside the button container
      if (ref.current && ref.current.contains(event.target)) {
        return;
      }
      // Check if click is inside the menu (for portal case)
      if (menuRef.current && menuRef.current.contains(event.target)) {
        return;
      }
      setOpen(false);
    }
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  // Calculate menu position and direction when opening
  useEffect(() => {
    if (open && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const menuHeight = 300; // max menu height
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;

      // Determine if menu should open upward
      const shouldOpenUp = spaceBelow < menuHeight && spaceAbove > spaceBelow;
      setOpenDirection(shouldOpenUp ? 'up' : 'down');

      if (usePortal) {
        setMenuPosition({
          top: shouldOpenUp ? rect.top + window.scrollY : rect.bottom + window.scrollY,
          left: align === 'left' ? rect.left + window.scrollX : rect.right + window.scrollX,
          openUp: shouldOpenUp,
        });
      }
    }
  }, [open, usePortal, align]);

  // Pass a closeDropdown function to children via context or props
  const closeDropdown = () => setOpen(false);

  // If children is a function, call it with closeDropdown, else clone children and inject closeDropdown as prop
  let renderedChildren;
  if (typeof children === 'function') {
    renderedChildren = children({ closeDropdown });
  } else {
    renderedChildren = React.Children.map(children, child =>
      React.isValidElement(child)
        ? cloneElement(child, { closeDropdown })
        : child
    );
  }

  const handleClick = () => {
    if (!disabled) {
      setOpen((o) => !o);
    }
  };

  const getMenuStyle = () => {
    const baseStyle = {
      background: '#fff',
      border: '1px solid #ccc',
      borderRadius: 4,
      minWidth: 150,
      boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
      maxHeight: 300,
      overflowY: 'auto',
    };

    if (usePortal) {
      return {
        ...baseStyle,
        position: 'fixed',
        zIndex: 10000,
        left: align === 'left' ? menuPosition.left : 'auto',
        right: align === 'right' ? window.innerWidth - menuPosition.left : 'auto',
        ...(menuPosition.openUp
          ? { bottom: window.innerHeight - menuPosition.top, top: 'auto' }
          : { top: menuPosition.top, bottom: 'auto' }
        ),
      };
    }

    return {
      ...baseStyle,
      position: 'absolute',
      zIndex: 1000,
      [align]: 0,
      ...(openDirection === 'up'
        ? { bottom: '100%', top: 'auto', marginBottom: 4 }
        : { top: '100%', bottom: 'auto', marginTop: 4 }
      ),
    };
  };

  const menu = open && (
    <div ref={menuRef} className={`dropdown-menu ${menuClassName}`} style={getMenuStyle()}>
      {renderedChildren}
    </div>
  );

  return (
    <div className={`dropdown ${className}`} ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        ref={buttonRef}
        className={buttonClassName || 'dropdown-toggle'}
        onClick={handleClick}
        disabled={disabled}
        style={
          buttonClassName
            ? undefined
            : {
                background: 'none',
                border: 'none',
                cursor: disabled ? 'not-allowed' : 'pointer',
                padding: 6,
                fontSize: 16,
                opacity: disabled ? 0.5 : 1,
              }
        }
        aria-haspopup="true"
        aria-expanded={open}
      >
        {label}
      </button>
      {usePortal ? ReactDOM.createPortal(menu, document.body) : menu}
    </div>
  );
};

export const DropdownItem = ({ onClick, children, closeDropdown }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      className="dropdown-item"
      onClick={e => {
        if (onClick) onClick(e);
        if (closeDropdown) closeDropdown();
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        padding: '10px 16px',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        fontSize: 14,
        backgroundColor: isHovered ? '#f5f5f5' : 'transparent',
        transition: 'background-color 0.15s ease',
      }}
      tabIndex={0}
      onKeyPress={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          if (onClick) onClick(e);
          if (closeDropdown) closeDropdown();
        }
      }}
      role="menuitem"
    >
      {children}
    </div>
  );
};
